import { useState, useMemo, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useCompanyProfile } from "@/hooks/useCompanyProfile";
import { usePermissions } from "@/hooks/usePermissions";
import { StatusBadge } from "@/components/controller/StatusBadge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, AlertTriangle, Eye, FileText, Upload } from "lucide-react";
import { InvoiceUploadDialog } from "@/components/controller/InvoiceUploadDialog";
import { InvoiceDetailDrawer } from "@/components/controller/InvoiceDetailDrawer";
import { toast } from "sonner";
import { format } from "date-fns";
import { handleRpcError } from "@/lib/rpcErrors";
import { ExportToolbar } from "@/components/shared/ExportToolbar";
import { exportPdf } from "@/lib/export";
import { InvoicePrintTemplate } from "@/components/shared/print-templates/InvoicePrintTemplate";
import { useIsMobile } from "@/hooks/use-mobile";
import { useFormDraft } from "@/hooks/useFormDraft";
import { formatQbDate, fmtCurrency, QbStatusCell, QbActionLinks, QbTypeBadge } from "@/components/controller/invoiceQbHelpers";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

interface Invoice {
  id: string;
  invoice_number: string;
  invoice_date: string;
  total: number;
  status: string;
  customer_id: string | null;
  customer_visible?: boolean;
  customers?: { name: string } | null;
}

interface BillableLine {
  id: string;
  sovLineId: string | null;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  lineTotal: number;
}

interface BillableExpense {
  id: string;
  description: string;
  amount: number;
  category: string;
}

interface Props {
  projectId: string;
  invoices: Invoice[];
  customers: { id: string; name: string }[];
  contractValue: number;
  onMutate: () => void;
  project?: any;
}

export const ProjectInvoicesTab = ({ projectId, invoices, customers, contractValue, onMutate, project }: Props) => {
  const isMobile = useIsMobile();
  const { can, isAdmin } = usePermissions();
  const { data: companyProfile } = useCompanyProfile();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [form, setForm] = useState({ invoice_number: "", invoice_date: "", customer_id: "" });
  const [notes, setNotes] = useState("");
  const [billableLines, setBillableLines] = useState<BillableLine[]>([]);
  const [selectedLines, setSelectedLines] = useState<Set<string>>(new Set());
  const [billableExpenses, setBillableExpenses] = useState<BillableExpense[]>([]);
  const [selectedExpenses, setSelectedExpenses] = useState<Set<string>>(new Set());
  const [loadingLines, setLoadingLines] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const canManageVisibility = isAdmin || can("manage_customers");

  // Draft persistence for invoice creation form
  const { saveDraft, loadDraft, clearDraft } = useFormDraft<any>(`invoice_new_${projectId}`);

  // Auto-save draft when form changes while dialog is open
  useEffect(() => {
    if (!open) return;
    saveDraft({
      form, notes,
      selectedLines: Array.from(selectedLines),
      selectedExpenses: Array.from(selectedExpenses),
    });
  }, [open, form, notes, selectedLines, selectedExpenses, saveDraft]);

  const fetchBillableLines = async () => {
    setLoadingLines(true);

    // Fetch bill lines to identify which SOV lines have confirmed work
    const { data: billLines } = await supabase
      .from("bill_lines")
      .select("id, description, quantity, unit_price, line_total, sov_line_id, bill_id, bills!inner(status, project_id)")
      .eq("bills.project_id", projectId);

    // Collect unique sov_line_ids from confirmed bills
    const confirmedSovIds = new Set<string>();
    const fallbackLines: BillableLine[] = [];
    for (const bl of (billLines ?? []) as any[]) {
      const billStatus = bl.bills?.status;
      if (!billStatus) continue;
      const isConfirmed = ["approved", "paid", "partially_paid"].includes(billStatus);
      if (!isConfirmed) continue;
      if (bl.sov_line_id) {
        confirmedSovIds.add(bl.sov_line_id);
      } else {
        // Fallback for bill lines not linked to SOV
        fallbackLines.push({
          id: bl.id,
          sovLineId: null,
          description: bl.description ?? "Line item",
          quantity: Number(bl.quantity) || 1,
          unit: "",
          unitPrice: Number(bl.unit_price) || 0,
          lineTotal: Number(bl.line_total) || 0,
        });
      }
    }

    // Fetch SOV line details for confirmed items
    const lines: BillableLine[] = [];
    if (confirmedSovIds.size > 0) {
      const { data: sovLines } = await supabase
        .from("sov_lines")
        .select("id, description, quantity, unit, unit_price, scheduled_value")
        .in("id", Array.from(confirmedSovIds));

      for (const sov of (sovLines ?? []) as any[]) {
        lines.push({
          id: sov.id,
          sovLineId: sov.id,
          description: sov.description ?? "Line item",
          quantity: Number(sov.quantity) || 1,
          unit: sov.unit ?? "",
          unitPrice: Number(sov.unit_price) || 0,
          lineTotal: Number(sov.scheduled_value) || 0,
        });
      }
    }

    // Add fallback lines (bill lines without SOV link)
    lines.push(...fallbackLines);
    setBillableLines(lines);
    setSelectedLines(new Set());

    // Fetch billable expenses
    const { data: expenses } = await supabase
      .from("project_expenses")
      .select("id, description, amount, category")
      .eq("project_id", projectId)
      .eq("status", "approved");

    const expList: BillableExpense[] = (expenses ?? []).map((e: any) => ({
      id: e.id, description: e.description ?? e.category, amount: Number(e.amount), category: e.category ?? "expense",
    }));
    setBillableExpenses(expList);
    setSelectedExpenses(new Set(expList.map((e) => e.id)));

    setLoadingLines(false);
  };

  const openDialog = () => {
    const draft = loadDraft();
    if (draft && (draft.form?.invoice_number || draft.notes)) {
      setForm(draft.form || { invoice_number: "", invoice_date: format(new Date(), "yyyy-MM-dd"), customer_id: project?.customer_id ?? "" });
      setNotes(draft.notes || "");
      // Selections will be re-applied after billable lines load
      toast.info("Restored your previous invoice draft");
    } else {
      setForm({ invoice_number: "", invoice_date: format(new Date(), "yyyy-MM-dd"), customer_id: project?.customer_id ?? "" });
      setNotes("");
    }
    setSelectedLines(new Set());
    setSelectedExpenses(new Set());
    setBillableLines([]);
    setBillableExpenses([]);
    setOpen(true);
    fetchBillableLines();
  };

  const toggleLine = (id: string) => setSelectedLines((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleExpense = (id: string) => setSelectedExpenses((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const selectAllLines = () => {
    const allIds = billableLines.map((l) => l.id);
    setSelectedLines((prev) => prev.size === allIds.length ? new Set() : new Set(allIds));
  };
  const selectAllExpenses = () => {
    const allIds = billableExpenses.map((e) => e.id);
    setSelectedExpenses((prev) => prev.size === allIds.length ? new Set() : new Set(allIds));
  };

  const selectedLineTotal = useMemo(() => billableLines.filter((l) => selectedLines.has(l.id)).reduce((s, l) => s + l.lineTotal, 0), [billableLines, selectedLines]);
  const selectedExpenseTotal = useMemo(() => billableExpenses.filter((e) => selectedExpenses.has(e.id)).reduce((s, e) => s + e.amount, 0), [billableExpenses, selectedExpenses]);
  const selectedTotal = selectedLineTotal + selectedExpenseTotal;

  const billedStatuses = ["approved", "sent", "paid", "partially_paid", "overdue"];
  const totalBilled = invoices.filter((i) => billedStatuses.includes(i.status)).reduce((s, i) => s + Number(i.total), 0);
  const totalPending = invoices.filter((i) => i.status === "draft").reduce((s, i) => s + Number(i.total), 0);
  const totalPaid = invoices.reduce((s, i) => s + Number((i as any).amount_paid || 0), 0);
  const overbilled = contractValue > 0 && totalBilled > contractValue;

  const toggleCustomerVisible = async (invId: string, current: boolean) => {
    const { error } = await supabase.from("invoices").update({ customer_visible: !current }).eq("id", invId);
    if (error) return toast.error(error.message);
    toast.success(!current ? "Shared with customer" : "Hidden from customer");
    onMutate();
  };

  const approve = async (id: string) => {
    setBusy(id);
    const { error } = await supabase.rpc("approve_invoice", { p_invoice_id: id });
    setBusy(null);
    if (error) return handleRpcError(error);
    toast.success("Invoice approved");
    onMutate();
  };

  const markSent = async (id: string) => {
    setBusy(id);
    const { error } = await supabase.rpc("transition_invoice_status", { p_invoice_id: id, p_new_status: "sent" });
    setBusy(null);
    if (error) return handleRpcError(error);
    toast.success("Invoice marked as sent");
    onMutate();
  };

  const [paymentDialog, setPaymentDialog] = useState<{ id: string; total: number; amountPaid: number } | null>(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentRef, setPaymentRef] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");

  const recordPayment = async () => {
    if (!paymentDialog) return;
    const amount = Number(paymentAmount);
    if (!amount || amount <= 0) return toast.error("Enter a valid amount");
    setBusy(paymentDialog.id);
    try {
      const { data, error } = await supabase.rpc("transition_invoice_status", {
        p_invoice_id: paymentDialog.id, p_new_status: "paid",
        p_payment_amount: amount, p_payment_ref: paymentRef || null, p_payment_method: paymentMethod || null,
      });
      if (error) throw error;
      const newStatus = (data as any)?.new_status;
      toast.success(newStatus === "paid" ? "Invoice paid in full" : "Partial payment recorded");
      onMutate();
      setPaymentDialog(null); setPaymentAmount(""); setPaymentRef(""); setPaymentMethod("");
    } catch (err: any) { handleRpcError(err); } finally { setBusy(null); }
  };

  const printInvoice = async (inv: any) => {
    const { data: lines } = await supabase.from("invoice_lines").select("description, quantity, unit_price, line_total").eq("invoice_id", inv.id);
    exportPdf(
      <InvoicePrintTemplate data={{
        invoice_number: inv.invoice_number, invoice_date: inv.invoice_date, due_date: inv.due_date,
        status: inv.status, total: inv.total, amount_paid: inv.amount_paid || 0,
        payment_method: inv.payment_method, payment_reference: inv.payment_reference,
        customer_name: inv.customers?.name, project_name: project?.name,
        company_name: companyProfile?.name, company_logo_url: companyProfile?.logo_url,
        notes: inv.notes,
        line_items: (lines ?? []).map((l: any) => ({ description: l.description, quantity: l.quantity, unit_price: l.unit_price, line_total: l.line_total })),
      }} />,
      `Invoice ${inv.invoice_number}`
    );
  };

  const buildHandlers = (inv: any) => ({
    onEdit: () => setSelectedInvoiceId(inv.id),
    onApprove: inv.status === "draft" && can("approve_invoices") ? () => approve(inv.id) : undefined,
    onSend: inv.status === "approved" ? () => markSent(inv.id) : undefined,
    onReceivePayment: ["approved", "sent", "partially_paid", "overdue"].includes(inv.status)
      ? () => setPaymentDialog({ id: inv.id, total: Number(inv.total), amountPaid: Number(inv.amount_paid) || 0 })
      : undefined,
    onPrint: () => printInvoice(inv),
    busy: busy === inv.id,
  });

  const buildPreviewData = () => {
    const selLines = billableLines.filter((l) => selectedLines.has(l.id));
    const selExps = billableExpenses.filter((e) => selectedExpenses.has(e.id));
    const lineItems = selLines.map((l) => ({
      description: l.description,
      quantity: l.quantity, unit_price: l.unitPrice, line_total: l.lineTotal, unit: l.unit,
    }));
    const expenseItems = selExps.map((e) => ({
      description: `${e.category.replace(/_/g, " ")} — ${e.description}`, quantity: 1, unit_price: e.amount, line_total: e.amount, unit: "",
    }));
    return {
      invoice_number: form.invoice_number || "DRAFT",
      invoice_date: form.invoice_date,
      due_date: null,
      status: "draft",
      total: selectedTotal,
      amount_paid: 0,
      customer_name: customers.find((c) => c.id === form.customer_id)?.name,
      project_name: project?.name,
      company_name: companyProfile?.name ?? project?.gc_name,
      company_logo_url: companyProfile?.logo_url,
      line_items: lineItems,
      expenses: expenseItems,
      notes: notes || undefined,
    };
  };

  const preview = () => {
    if (selectedLines.size === 0 && selectedExpenses.size === 0) return toast.error("Select at least one item");
    exportPdf(<InvoicePrintTemplate data={buildPreviewData()} />, `Invoice Preview`);
  };

  const create = async () => {
    if (!form.invoice_date) return toast.error("Date required");
    if (selectedLines.size === 0 && selectedExpenses.size === 0) return toast.error("Select at least one item");
    setBusy("new");

    const { data: companyId } = await supabase.rpc("my_company_id");
    const { data: inv, error } = await supabase.from("invoices").insert({
      project_id: projectId, company_id: companyId!, invoice_number: form.invoice_number || null,
      invoice_date: form.invoice_date, customer_id: form.customer_id || null, notes: notes || null,
      subtotal: selectedTotal, total: selectedTotal,
    }).select("id").single();

    if (error) { setBusy(null); return toast.error(error.message); }

    // Build all line items
    const allItems: any[] = [];
    const selLines = billableLines.filter((l) => selectedLines.has(l.id));
    for (const l of selLines) {
      allItems.push({
        invoice_id: inv!.id, company_id: companyId!,
        description: l.description,
        quantity: l.quantity, unit_price: l.unitPrice,
        sov_line_id: l.sovLineId ?? null,
      });
    }
    const selExps = billableExpenses.filter((e) => selectedExpenses.has(e.id));
    for (const e of selExps) {
      allItems.push({
        invoice_id: inv!.id, company_id: companyId!,
        description: `${e.category.replace(/_/g, " ")} — ${e.description}`,
        quantity: 1, unit_price: e.amount,
      });
    }
    if (allItems.length > 0) {
      const { error: lineError } = await supabase.from("invoice_lines").insert(allItems);
      if (lineError) toast.error("Invoice created but line items failed: " + lineError.message);
    }

    setBusy(null);
    toast.success("Invoice created");
    clearDraft();
    setOpen(false);
    onMutate();
  };

  return (
    <div className="space-y-4">
      {overbilled && (
        <Alert className="bg-destructive/10 border-destructive/30">
          <AlertTriangle className="h-4 w-4 text-destructive" />
          <AlertDescription className="text-destructive">
            Overbilling alert: Total billed ({fmt(totalBilled)}) exceeds contract value ({fmt(contractValue)}).
          </AlertDescription>
        </Alert>
      )}

      <div className="flex justify-end gap-2">
        {invoices.length > 0 && <ExportToolbar entityType="invoices" data={invoices} />}
        {(isAdmin || can("create_invoices")) && (
          <>
            <Button size="sm" variant="outline" onClick={() => setUploadOpen(true)}><Upload className="mr-1 h-4 w-4" />Upload Invoice</Button>
            <Button size="sm" onClick={openDialog}><Plus className="mr-1 h-4 w-4" />New Invoice</Button>
          </>
        )}
      </div>

      {!invoices.length ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground mb-2">No invoices yet</p>
          <Button variant="outline" size="sm" onClick={openDialog}>Create your first Invoice</Button>
        </div>
      ) : (
        <>
          {isMobile ? (
            /* ── Mobile Card View (QB-style) ── */
            <div className="space-y-3">
              {invoices.map((inv) => (
                <div key={inv.id} className="border rounded-lg p-3 space-y-2 cursor-pointer hover:bg-muted/50" onClick={() => setSelectedInvoiceId(inv.id)}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-muted-foreground">{formatQbDate(inv.invoice_date)}</span>
                        <QbTypeBadge />
                        <span className="font-medium text-sm">{inv.invoice_number || "—"}</span>
                      </div>
                      {(inv as any).notes && <p className="text-xs text-muted-foreground line-clamp-1 mt-1">{(inv as any).notes}</p>}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-lg font-bold text-primary">{fmtCurrency(Number(inv.total))}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2 pt-1">
                    <QbStatusCell inv={inv} />
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      {canManageVisibility && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Eye className="h-3.5 w-3.5" />
                          <Switch checked={!!inv.customer_visible} onCheckedChange={() => toggleCustomerVisible(inv.id, !!inv.customer_visible)} />
                        </div>
                      )}
                      <QbActionLinks inv={inv} handlers={buildHandlers(inv)} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* ── Desktop Table View (QB-style) ── */
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>DATE</TableHead>
                  <TableHead>TYPE</TableHead>
                  <TableHead>NO.</TableHead>
                  <TableHead>MEMO</TableHead>
                  <TableHead className="text-right">AMOUNT</TableHead>
                  <TableHead>STATUS</TableHead>
                  {canManageVisibility && <TableHead className="text-center"><Eye className="h-4 w-4 mx-auto" /></TableHead>}
                  <TableHead>ACTION</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((inv, i) => (
                  <TableRow key={inv.id} className={`cursor-pointer ${i % 2 === 1 ? "bg-muted/30" : ""}`} onClick={() => setSelectedInvoiceId(inv.id)}>
                    <TableCell className="text-sm">{formatQbDate(inv.invoice_date)}</TableCell>
                    <TableCell><QbTypeBadge /></TableCell>
                    <TableCell className="font-medium text-sm">{inv.invoice_number || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-xs">
                      <span className="line-clamp-1" title={(inv as any).notes ?? ""}>{(inv as any).notes || "—"}</span>
                    </TableCell>
                    <TableCell className="text-right font-medium text-primary">{fmtCurrency(Number(inv.total))}</TableCell>
                    <TableCell><QbStatusCell inv={inv} /></TableCell>
                    {canManageVisibility && (
                      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                        <Switch checked={!!inv.customer_visible} onCheckedChange={() => toggleCustomerVisible(inv.id, !!inv.customer_visible)} className="mx-auto" />
                      </TableCell>
                    )}
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <QbActionLinks inv={inv} handlers={buildHandlers(inv)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <div className="flex flex-wrap justify-between gap-2 text-sm text-muted-foreground">
            <span>Pending: <span className="font-semibold text-foreground">{fmt(totalPending)}</span></span>
            <span>Billed: <span className="font-semibold text-foreground">{fmt(totalBilled)}</span></span>
            <span>Collected: <span className="font-semibold text-foreground">{fmt(totalPaid)}</span></span>
          </div>
        </>
      )}

      {/* New Invoice Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Invoice</DialogTitle>
            <DialogDescription>Create an invoice from approved bill line items and expenses.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Header fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Invoice Number <span className="text-muted-foreground text-xs">(optional)</span></Label>
                <Input value={form.invoice_number} onChange={(e) => setForm((p) => ({ ...p, invoice_number: e.target.value }))} placeholder="Leave blank to auto-generate" />
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <Input type="date" value={form.invoice_date} onChange={(e) => setForm((p) => ({ ...p, invoice_date: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Customer</Label>
              <Input value={customers.find((c) => c.id === form.customer_id)?.name ?? ""} readOnly className="bg-muted" />
            </div>

            {/* Billable SOV Lines */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Billable Line Items</Label>
                {billableLines.length > 0 && (
                  <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={selectAllLines}>
                    {selectedLines.size === billableLines.length ? "Deselect All" : "Select All"}
                  </Button>
                )}
              </div>
              {loadingLines ? (
                <p className="text-sm text-muted-foreground py-4 text-center">Loading billable items…</p>
              ) : billableLines.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No billable items found.</p>
              ) : (
                <div className="border rounded-md divide-y max-h-48 overflow-y-auto">
                  {billableLines.map((line) => (
                    <div key={line.id} className="flex items-center gap-3 px-3 py-2 hover:bg-muted/50 cursor-pointer" onClick={() => toggleLine(line.id)}>
                      <Checkbox checked={selectedLines.has(line.id)} onCheckedChange={() => toggleLine(line.id)} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{line.description}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {new Intl.NumberFormat("en-US").format(line.quantity)}{line.unit ? ` ${line.unit}` : ""} × {fmt(line.unitPrice)}{line.unit ? `/${line.unit}` : ""}
                        </p>
                      </div>
                      <span className="text-sm font-semibold whitespace-nowrap">{fmt(line.lineTotal)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Billable Expenses */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Billable Expenses & Reimbursements</Label>
                {billableExpenses.length > 0 && (
                  <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={selectAllExpenses}>
                    {selectedExpenses.size === billableExpenses.length ? "Deselect All" : "Select All"}
                  </Button>
                )}
              </div>
              {loadingLines ? (
                <p className="text-sm text-muted-foreground py-4 text-center">Loading…</p>
              ) : billableExpenses.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No billable expenses found.</p>
              ) : (
                <div className="border rounded-md divide-y max-h-40 overflow-y-auto">
                  {billableExpenses.map((exp) => (
                    <div key={exp.id} className="flex items-center gap-3 px-3 py-2 hover:bg-muted/50 cursor-pointer" onClick={() => toggleExpense(exp.id)}>
                      <Checkbox checked={selectedExpenses.has(exp.id)} onCheckedChange={() => toggleExpense(exp.id)} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{exp.description}</p>
                        <Badge variant="secondary" className="text-xs mt-0.5">{exp.category.replace(/_/g, " ")}</Badge>
                      </div>
                      <span className="text-sm font-semibold whitespace-nowrap">{fmt(exp.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes <span className="text-muted-foreground text-xs">(appears on invoice)</span></Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Payment terms, thank you message, special instructions…" rows={3} />
            </div>

            {/* Totals */}
            {(selectedLines.size > 0 || selectedExpenses.size > 0) && (
              <div className="border-t pt-3 space-y-1">
                {selectedLineTotal > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Services ({selectedLines.size} items)</span>
                    <span className="font-medium">{fmt(selectedLineTotal)}</span>
                  </div>
                )}
                {selectedExpenseTotal > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Expenses ({selectedExpenses.size} items)</span>
                    <span className="font-medium">{fmt(selectedExpenseTotal)}</span>
                  </div>
                )}
                <div className="flex justify-between text-base font-bold pt-1 border-t">
                  <span>Invoice Total</span>
                  <span>{fmt(selectedTotal)}</span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="secondary" onClick={preview} disabled={selectedLines.size === 0 && selectedExpenses.size === 0}>
              <FileText className="mr-1 h-4 w-4" />Preview
            </Button>
            <Button disabled={busy === "new" || (selectedLines.size === 0 && selectedExpenses.size === 0)} onClick={create}>
              {busy === "new" ? "Saving…" : "Create Invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={!!paymentDialog} onOpenChange={(o) => { if (!o) setPaymentDialog(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>{paymentDialog && `Remaining: ${fmtCurrency(paymentDialog.total - paymentDialog.amountPaid)}`}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Amount</Label><Input type="number" placeholder="0.00" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} /></div>
            <div className="space-y-1">
              <Label>Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger><SelectValue placeholder="Select method" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="check">Check</SelectItem><SelectItem value="ach">ACH</SelectItem>
                  <SelectItem value="wire">Wire</SelectItem><SelectItem value="credit_card">Credit Card</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem><SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Reference (optional)</Label><Input placeholder="Check #, ACH ref, etc." value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialog(null)}>Cancel</Button>
            <Button disabled={busy !== null} onClick={recordPayment}>{busy ? "Saving…" : "Record"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InvoiceUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        projectId={projectId}
        onSuccess={onMutate}
      />

      <InvoiceDetailDrawer
        invoiceId={selectedInvoiceId}
        open={!!selectedInvoiceId}
        onOpenChange={(o) => { if (!o) setSelectedInvoiceId(null); }}
        onMutate={onMutate}
      />
    </div>
  );
};
